/* eslint-disable complexity */
import { Fragment, type ReactNode } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import classNames from 'classnames';

import { useFragmentContext } from '@jsmdg/react-fragment-scripts/fragment';
import { TenantEnum } from '@jsmdg/tenancy';
import { Wysiwyg } from '@jsmdg/yoshi';
import { ContentType } from '../../../shared/enums/contentType';
import { type PageType } from '../../../shared/enums/pageType';
import { type FragmentContext } from '../../../shared/types/fragmentContext';
import { type PageContent as PageContentType } from '../../../shared/types/pageContent';
import { type PageDocument } from '../../../shared/types/pageDocument';
import { TrackingCreativePrefix } from '../../enums/trackingCreativePrefix';
import { useCreatives } from '../../hooks/useCreatives';
import { BestsellerSlider } from '../BestsellerSlider';
import { BestsellerValueVoucherSlider } from '../BestsellerValueVoucherSlider';
import { BoxGifSlider } from '../BoxGifSlider';
import { Categories } from '../Categories';
import { ExperienceGuaranteeWrapper } from '../ExperienceGuaranteeWrapper';
import { HeroStage } from '../HeroStage';
import { HighlightSlider } from '../HighlightSlider';
import { ImageAndText } from '../ImageAndText';
import { LastSeenProductSlider } from '../LastSeenProductSlider';
import { PageSlider } from '../PageSlider';
import { PageTiles } from '../PageTiles';
import styles from './Page.module.scss';

type PageContentProps = {
    readonly document?: PageDocument;
    readonly pageType: PageType;
};

const messages = defineMessages({
    a11yPreviousSlide: { defaultMessage: 'Zur vorherigen Seite' },
    a11yNextSlide: { defaultMessage: 'Zur nächsten Seite' },
});

const PageContent = ({ document, pageType }: PageContentProps): JSX.Element | null => {
    const intl = useIntl();
    const { dynamicYieldHeroStageCreatives, heroStageCreatives, navigationCreatives } =
        useCreatives();
    const { isMydays, tenant } = useFragmentContext<FragmentContext>();
    const isChTenant = tenant === TenantEnum.MD_CH;

    const hasNavigationCreative = navigationCreatives.length > 0;

    let verticalPosition = hasNavigationCreative ? 1 : 0;

    const renderPageContentItems = (content: PageContentType, index: number): ReactNode => {
        switch (content.type) {
            case ContentType.PageSlider:
                verticalPosition += 1;
                return (
                    <PageSlider
                        slides={content.data || []}
                        headline={content.title}
                        verticalPosition={verticalPosition}
                        hasNavigationCreative={hasNavigationCreative}
                        pageType={pageType}
                        crossLinkPageTitle={content?.crossLinkPageTitle}
                        crossLinkPageRef={content?.crossLinkPageRef}
                        trackingPrefix={TrackingCreativePrefix.PageSlider}
                        trackingPath={document?.trackingPath}
                        a11yNextSlide={intl.formatMessage(messages.a11yNextSlide)}
                        a11yPreviousSlide={intl.formatMessage(messages.a11yPreviousSlide)}
                    />
                );

            case ContentType.Wysiwyg:
                return <Wysiwyg content={content.value} />;

            case ContentType.ImageAndText:
                return (
                    <ImageAndText
                        image={content.image}
                        imagePosition={content.imagePosition}
                        content={content.content}
                        link={content.link}
                        target={content.target || ''}
                        linkLabel={content.linkLabel}
                        lazyLoad
                    />
                );
            case ContentType.ImageSlider: {
                verticalPosition += 1;
                if (!isMydays) verticalPosition += 1;

                return (
                    <HeroStage
                        galleryImages={content.galleryImages}
                        categories={content.categories}
                        heroStageCreatives={heroStageCreatives}
                        dynamicYieldHeroStageCreatives={dynamicYieldHeroStageCreatives}
                        hasNavigationCreative={hasNavigationCreative}
                        verticalPosition={verticalPosition}
                    />
                );
            }

            case ContentType.LastSeen:
                verticalPosition += 1;
                return (
                    <LastSeenProductSlider
                        lazyLoad
                        pageType={pageType}
                        verticalPosition={verticalPosition}
                    />
                );
            case ContentType.PageTileWidget:
                verticalPosition += 1;

                return (
                    <>
                        <PageTiles
                            identifier={`${content.type}-${index}`}
                            tiles={content.pageTiles}
                            pageType={pageType}
                            isNextToNavigation={index < 2}
                            verticalPosition={verticalPosition}
                            hasNavigationCreative={hasNavigationCreative}
                            trackingPrefix={TrackingCreativePrefix.PageTile}
                        />
                        {!isMydays && (
                            <Categories
                                pageType={pageType}
                                verticalPosition={verticalPosition}
                                hasNavigationCreative={hasNavigationCreative}
                                withBlockMargin
                            />
                        )}
                    </>
                );

            case ContentType.Bestseller: {
                verticalPosition += 1;
                const valueVoucherSearchResult = content.valueVoucherSearchResult;
                const hasValueVoucherSlider = Boolean(valueVoucherSearchResult?.items.length);

                if (hasValueVoucherSlider) {
                    verticalPosition += 1;
                }

                const bestsellerContent = (
                    <>
                        <BestsellerSlider
                            initialSearchResult={content.initialSearchResult}
                            moreResultsLink={content.moreResultsLink || ''}
                            lazyLoad
                            pageType={pageType}
                            verticalPosition={verticalPosition}
                        />
                        {hasValueVoucherSlider && valueVoucherSearchResult ? (
                            <BestsellerValueVoucherSlider
                                valueVoucherSearchResult={valueVoucherSearchResult}
                            />
                        ) : null}
                    </>
                );

                return bestsellerContent;
            }

            case ContentType.Highlight:
                verticalPosition += 1;
                return (
                    <HighlightSlider
                        initialSearchResult={content.initialSearchResult}
                        moreResultsLink={content.moreResultsLink || ''}
                        lazyLoad
                        pageType={pageType}
                        verticalPosition={verticalPosition}
                    />
                );

            default:
                return <div>Unknown element</div>;
        }
    };

    const firstPageSliderIndex =
        document?.contents?.findIndex(content => content.type === ContentType.PageSlider) ?? -1;

    if (!document?.contents) return null;

    return (
        <>
            {document?.contents.map((content, index) => {
                const key = `row-${index}`;
                const isImageSliderOrWysiwyg =
                    content.type === ContentType.ImageSlider ||
                    content.type === ContentType.Wysiwyg;

                return (
                    <Fragment key={key}>
                        <div className="grid">
                            <div
                                className={classNames('g-col-12', styles.row, styles.homeRow, {
                                    'mb-0': isImageSliderOrWysiwyg,
                                    'mt-0': content.type === ContentType.ImageSlider,
                                })}
                            >
                                {renderPageContentItems(content, index)}
                            </div>
                        </div>
                        {index === firstPageSliderIndex && (
                            <>
                                {!isChTenant && document?.boxGifSliderData && (
                                    <div className="grid">
                                        <div
                                            className={classNames(
                                                'g-col-12',
                                                styles.row,
                                                styles.homeRow,
                                            )}
                                        >
                                            <BoxGifSlider
                                                boxGifSliderData={document.boxGifSliderData}
                                            />
                                        </div>
                                    </div>
                                )}
                                <div className="grid">
                                    <div
                                        className={classNames(
                                            'g-col-12',
                                            styles.row,
                                            styles.homeRow,
                                        )}
                                    >
                                        <ExperienceGuaranteeWrapper />
                                    </div>
                                </div>
                            </>
                        )}
                    </Fragment>
                );
            })}
        </>
    );
};

export { PageContent };
